/* ── exportSession ───────────────────────────────────────────
   Export the ENTIRE session (all messages + citations) as a
   single PDF via the print-to-PDF approach.

   Creates a hidden styled div, opens it in a new window, and
   triggers window.print().

   Includes: timestamp, session title, each Q&A pair, cited
   clauses, queryId + date for audit trail.
   ────────────────────────────────────────────────────────────── */

import type { ChatMessage } from '../types';

interface ExportOptions {
  sessionTitle?: string;
  sessionId?: string;
}

/** Escape HTML special chars */
function esc(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Simple markdown → HTML for export (handles headers, lists, bold, blockquotes) */
function mdToHtml(md: string): string {
  return md
    .split('\n')
    .map((line) => {
      // Strip FOLLOW_UPS comment blocks
      if (line.includes('<!-- FOLLOW_UPS') || line.includes('<!-- /FOLLOW_UPS')) return '';

      const trimmed = line.trim();
      if (trimmed.startsWith('#### ')) return `<h4>${esc(trimmed.slice(5))}</h4>`;
      if (trimmed.startsWith('### ')) return `<h3>${esc(trimmed.slice(4))}</h3>`;
      if (trimmed.startsWith('## ')) return `<h2>${esc(trimmed.slice(3))}</h2>`;
      if (trimmed.startsWith('# ')) return `<h1>${esc(trimmed.slice(2))}</h1>`;
      if (trimmed.startsWith('> ')) return `<blockquote>${esc(trimmed.slice(2))}</blockquote>`;
      if (trimmed.startsWith('- ') || trimmed.startsWith('* '))
        return `<li>${esc(trimmed.slice(2))}</li>`;
      if (/^\d+\.\s/.test(trimmed))
        return `<li>${esc(trimmed.replace(/^\d+\.\s/, ''))}</li>`;
      if (trimmed === '') return '<br/>';
      return `<p>${esc(trimmed)}</p>`;
    })
    .join('\n')
    // Bold
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Inline code
    .replace(/`(.+?)`/g, '<code>$1</code>')
    // Strip citation badges [N]
    .replace(/\[(\d+)\]/g, '<sup style="color:#3b82f6;font-size:10px;">[$1]</sup>');
}

export async function exportSession(
  messages: ChatMessage[],
  options: ExportOptions = {},
): Promise<void> {
  const title = options.sessionTitle || 'Compliance Hub Session';
  const now = new Date();
  const dateStr = now.toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const timeStr = now.toLocaleTimeString('en-AU', {
    hour: '2-digit',
    minute: '2-digit',
  });

  // Build Q&A pairs
  const qaPairsHtml: string[] = [];
  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    if (msg.role === 'user') {
      qaPairsHtml.push(`
        <div class="qa-pair">
          <div class="query">
            <div class="label">Q</div>
            <div class="content">${esc(msg.content)}</div>
          </div>
      `);
    } else if (msg.role === 'assistant') {
      // Strip FOLLOW_UPS blocks from content
      const cleanContent = msg.content
        .replace(/<!--\s*FOLLOW_UPS\s*-->[\s\S]*?<!--\s*\/FOLLOW_UPS\s*-->/, '')
        .trim();

      const citationsHtml = msg.citations.length > 0
        ? `<div class="citations">
            <div class="citations-header">Sources (${msg.citations.length})</div>
            <ol>${msg.citations.map((c) =>
              `<li><strong>${esc(c.standard)}</strong> — §${esc(c.clause)}${
                c.text ? `<br/><span class="clause-text">${esc(c.text.slice(0, 300))}${c.text.length > 300 ? '…' : ''}</span>` : ''
              }</li>`
            ).join('')}</ol>
          </div>`
        : '';

      const auditLine = msg.queryId
        ? `<div class="audit-id">Query ID: ${esc(msg.queryId)}</div>`
        : '';

      qaPairsHtml.push(`
          <div class="response">
            <div class="label">A</div>
            <div class="content">${mdToHtml(cleanContent)}</div>
            ${citationsHtml}
            ${auditLine}
          </div>
        </div>
      `);
    }
  }

  const sessionIdLine = options.sessionId
    ? `<p class="meta">Session ID: ${esc(options.sessionId)}</p>`
    : '';

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <title>${esc(title)} — Export</title>
  <style>
    * { box-sizing: border-box; }
    body {
      font-family: -apple-system, system-ui, 'Segoe UI', sans-serif;
      max-width: 800px;
      margin: 40px auto;
      padding: 0 24px;
      color: #1e293b;
      line-height: 1.65;
      font-size: 13px;
    }
    .export-header {
      border-bottom: 2px solid #e2e8f0;
      padding-bottom: 12px;
      margin-bottom: 24px;
    }
    .export-header h1 {
      font-size: 18px;
      color: #3b82f6;
      margin: 0 0 4px;
    }
    .meta {
      color: #64748b;
      font-size: 11px;
      margin: 2px 0;
    }
    .qa-pair {
      margin-bottom: 28px;
      border-left: 3px solid #e2e8f0;
      padding-left: 16px;
    }
    .label {
      display: inline-block;
      font-weight: 700;
      font-size: 11px;
      color: #fff;
      background: #3b82f6;
      border-radius: 4px;
      padding: 1px 6px;
      margin-bottom: 4px;
      letter-spacing: 0.04em;
    }
    .query .label { background: #6366f1; }
    .response .label { background: #059669; }
    .query { margin-bottom: 12px; }
    .response { margin-top: 8px; }
    .content { margin-top: 4px; }
    .content p { margin: 4px 0; }
    .content h2, .content h3 { margin-top: 12px; font-size: 14px; }
    .content blockquote {
      border-left: 3px solid #3b82f6;
      margin: 8px 0;
      padding: 6px 12px;
      background: #f8fafc;
    }
    .content code {
      background: #f1f5f9;
      padding: 1px 4px;
      border-radius: 3px;
      font-size: 12px;
    }
    .citations {
      margin-top: 12px;
      padding: 10px 12px;
      background: #f8fafc;
      border-radius: 6px;
      border: 1px solid #e2e8f0;
    }
    .citations-header {
      font-weight: 600;
      font-size: 12px;
      color: #475569;
      margin-bottom: 6px;
    }
    .citations ol { margin: 0; padding-left: 18px; }
    .citations li { margin-bottom: 6px; font-size: 12px; }
    .clause-text { color: #64748b; font-size: 11px; }
    .audit-id {
      margin-top: 8px;
      font-size: 10px;
      color: #94a3b8;
      font-family: ui-monospace, monospace;
    }
    .footer {
      margin-top: 40px;
      padding-top: 12px;
      border-top: 1px solid #e2e8f0;
      font-size: 10px;
      color: #94a3b8;
      text-align: center;
    }
    @media print {
      body { margin: 16px; }
      .qa-pair { break-inside: avoid; }
    }
  </style>
</head>
<body>
  <div class="export-header">
    <h1>${esc(title)}</h1>
    <p class="meta">Exported: ${dateStr} at ${timeStr}</p>
    ${sessionIdLine}
    <p class="meta">${messages.filter((m) => m.role === 'user').length} queries · ${messages.filter((m) => m.role === 'assistant').length} responses</p>
  </div>

  ${qaPairsHtml.join('\n')}

  <div class="footer">
    Compliance Hub · Research aid only · Verify against official standards · ${dateStr}
  </div>
</body>
</html>`;

  const blob = new Blob([html], { type: 'text/html' });
  const filename = `compliance-hub-session-${now.toISOString().slice(0, 10)}.html`;

  // Detect iOS Safari — window.open() fails silently on iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  // A4 (2026-06-20): native share sheet removed — export is simplified to
  // Clipboard / PDF / HTML only. We go straight to print (PDF) or HTML download;
  // no social/native-share destinations.

  // iOS Safari: inject a print-optimised iframe and trigger window.print()
  // window.open() fails silently on iOS Safari; this approach works reliably.
  if (isIOS) {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.top = '-10000px';
    iframe.style.left = '-10000px';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (iframeDoc) {
      iframeDoc.open();
      iframeDoc.write(html);
      iframeDoc.close();

      // Give iOS a moment to render the content
      await new Promise((resolve) => setTimeout(resolve, 600));

      try {
        iframe.contentWindow?.print();
      } catch {
        // If print fails, fall back to download
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        a.click();
        URL.revokeObjectURL(a.href);
      }

      // Clean up iframe after a delay
      setTimeout(() => {
        try { document.body.removeChild(iframe); } catch { /* ignore */ }
      }, 2000);
      return;
    }
    // If iframe doc not accessible, fall through to desktop method
    try { document.body.removeChild(iframe); } catch { /* ignore */ }
  }

  const url = URL.createObjectURL(blob);
  const w = window.open(url, '_blank');
  if (w) {
    w.onload = () => {
      setTimeout(() => {
        w.print();
        URL.revokeObjectURL(url);
      }, 400);
    };
  } else {
    // Popup blocked — fall back to download
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }
}
